import React, { useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import useModalNovaRemessaHook from '../../hooks/useModalNovaRemessaHook';
import './ModalNovaRemessa.css';

const ModalNovaRemessa = ({ aberto, onFechar, abrirModal, onRemessaCriada }) => {
  const {
    empresas,
    empresaSelecionada,
    contaCobranca,
    manterSacados,
    naoProtestar,
    trocaCarteira,
    baixarTitulos,
    permissoesConta,
    contasDisponiveis,
    criando,
    sucesso,
    podeCriar,
    carregandoEmpresas,
    carregandoContas,
    handleCriarRemessa,
    handleChangeEmpresa,
    handleChangeContaCobranca,
    handleChangeManterSacados,
    handleChangeNaoProtestar,
    handleChangeTrocaCarteira,
    handleChangeBaixarTitulos,
    resetarEstado
  } = useModalNovaRemessaHook({ onFechar, abrirModal, onRemessaCriada });

  useEffect(() => {
    if (!aberto) {
      resetarEstado();
    }
  }, [aberto, resetarEstado]);

  return (
    <Dialog
      open={aberto}
      onClose={criando ? undefined : onFechar}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: 'modal-nova-remessa-paper' }}
    >
      {/* Header */}
      <Box className="modal-nova-remessa-header">
        <Box className="modal-nova-remessa-header-titulo">
          <AddCircleOutlineIcon color="primary" />
          <Typography variant="h6" className="modal-nova-remessa-titulo">
            Nova Remessa
          </Typography>
        </Box>
        <IconButton onClick={onFechar} disabled={criando} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Divider />

      {/* Conteúdo */}
      <DialogContent className="modal-nova-remessa-conteudo">
        {sucesso && (
          <Alert severity="success" className="modal-nova-remessa-alerta">
            Remessa criada com sucesso!
          </Alert>
        )}

        {/* Empresa */}
        <Box className="modal-campo-grupo">
          <Typography variant="subtitle2" className="modal-campo-label">
            Empresa *
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={empresaSelecionada}
              onChange={handleChangeEmpresa}
              displayEmpty
              disabled={criando || carregandoEmpresas}
              endAdornment={carregandoEmpresas ? <CircularProgress size={20} sx={{ mr: 2 }} /> : null}
              renderValue={
                empresaSelecionada !== ''
                  ? undefined
                  : () => <span className="modal-placeholder">Selecione a empresa</span>
              }
            >
              <MenuItem value="">
                <em>Nenhuma</em>
              </MenuItem>
              {empresas.map(empresa => (
                <MenuItem key={empresa.codigo} value={empresa.codigo}>
                  {empresa.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" className="modal-helper-text">
            Selecione a empresa que origina a remessa
          </Typography>
        </Box>

        {/* Conta de Cobrança */}
        <Box className="modal-campo-grupo">
          <Typography variant="subtitle2" className="modal-campo-label">
            Conta de Cobrança *
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={contaCobranca}
              onChange={handleChangeContaCobranca}
              displayEmpty
              disabled={!empresaSelecionada || criando || carregandoContas}
              endAdornment={carregandoContas ? <CircularProgress size={20} sx={{ mr: 2 }} /> : null}
              renderValue={
                contaCobranca !== ''
                  ? undefined
                  : () => <span className="modal-placeholder">Selecione a conta</span>
              }
            >
              <MenuItem value="">
                <em>Nenhuma</em>
              </MenuItem>
              {contasDisponiveis.map(conta => (
                <MenuItem key={conta.codigo} value={conta.codigo}>
                  {conta.label || conta.descricao}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="caption" className="modal-helper-text">
            Conta bancária associada à empresa selecionada
          </Typography>
        </Box>

        {/* Não protestar títulos */}
        {permissoesConta.permite_nao_protestar && (
          <Box className="modal-checkbox-box">
            <FormControlLabel
              control={
                <Checkbox
                  checked={naoProtestar}
                  onChange={handleChangeNaoProtestar}
                  disabled={criando}
                  size="small"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" className="modal-checkbox-titulo">
                    Não protestar títulos
                  </Typography>
                  <Typography variant="caption" className="modal-checkbox-descricao">
                    Ao marcar esta opção, os títulos desta remessa não serão protestados.
                  </Typography>
                </Box>
              }
            />
          </Box>
        )}

        {/* Troca de carteira */}
        {permissoesConta.permite_troca_carteira && (
          <Box className="modal-checkbox-box">
            <FormControlLabel
              control={
                <Checkbox
                  checked={trocaCarteira}
                  onChange={handleChangeTrocaCarteira}
                  disabled={criando}
                  size="small"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" className="modal-checkbox-titulo">
                    Troca de carteira
                  </Typography>
                  <Typography variant="caption" className="modal-checkbox-descricao">
                    Ao marcar esta opção, será realizada a troca de carteira na remessa.
                  </Typography>
                </Box>
              }
            />
          </Box>
        )}

        {/* Remessa de baixa de títulos */}
        {permissoesConta.permite_baixar_titulos && (
          <Box className="modal-checkbox-box">
            <FormControlLabel
              control={
                <Checkbox
                  checked={baixarTitulos}
                  onChange={handleChangeBaixarTitulos}
                  disabled={criando}
                  size="small"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" className="modal-checkbox-titulo">
                    Remessa de baixa de títulos
                  </Typography>
                  <Typography variant="caption" className="modal-checkbox-descricao">
                    Ao marcar esta opção, será gerada uma remessa de baixa de títulos.
                  </Typography>
                </Box>
              }
            />
          </Box>
        )}

        {/* Manter sacados originais */}
        {permissoesConta.permite_manter_sacados_originais && (
          <Box className="modal-checkbox-box">
            <FormControlLabel
              control={
                <Checkbox
                  checked={manterSacados}
                  onChange={handleChangeManterSacados}
                  disabled={criando}
                  size="small"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" className="modal-checkbox-titulo">
                    Manter sacados originais
                  </Typography>
                  <Typography variant="caption" className="modal-checkbox-descricao">
                    Ao marcar esta opção, os sacados cadastrados originalmente serão mantidos na remessa.
                  </Typography>
                </Box>
              }
            />
          </Box>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions className="modal-nova-remessa-footer">
        <Button onClick={onFechar} disabled={criando} className="modal-btn-cancelar">
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={() => handleCriarRemessa(contaCobranca, 'E')}
          disabled={!podeCriar || criando || sucesso}
          startIcon={criando ? <CircularProgress size={18} color="inherit" /> : <AddCircleOutlineIcon />}
          className="modal-btn-criar"
        >
          {criando ? 'Criando...' : 'Criar remessa'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalNovaRemessa;
